import { useState } from 'react';
import { ApiError, api } from '../../api/client.js';
import { Confirm, DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, timeAgo } from '../../lib/format.js';
import { whatsappLink } from '../../lib/whatsapp.js';

/**
 * Notices to everybody on the landlord's leases.
 *
 * Deliberately not the messaging screen: this is one thing said once to a whole
 * compound, and the only thing worth tracking about it is how many people have
 * actually read it.
 */
const ComposeModal = ({ properties, tenantCount, notice = null, onSubmit, onClose }) => {
  const editing = Boolean(notice);
  const [form, setForm] = useState({
    title: notice?.title || '',
    body: notice?.body || '',
    audience: notice?.audience || 'all_tenants',
    propertyId: notice?.propertyId ? String(notice.propertyId) : '',
    urgent: notice?.urgent || false,
    expiresOn: notice?.expiresOn || ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        title: form.title.trim(),
        body: form.body.trim(),
        urgent: form.urgent,
        expiresOn: form.expiresOn || null,
        // Who it goes to is fixed once it has been sent: those people have
        // already been told, so only a new notice can reach anybody else.
        ...(editing
          ? {}
          : {
              audience: form.audience,
              propertyId: form.audience === 'property' ? Number(form.propertyId) : null
            })
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Change this notice' : 'Put out a notice'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="announcement-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending...' : editing ? 'Save the changes' : 'Send it'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <p className="modal-lead">
        {editing
          ? 'They have already been told. Changing it updates what they see on their dashboard, but the email that went out cannot be recalled.'
          : 'It goes on their dashboard the next time they sign in, into their notifications, and to their inbox. You get a copy yourself, and this page then shows what the mail server said about each one.'}
      </p>

      <form id="announcement-form" className="stack" onSubmit={submit}>
        <Field label="Heading" hint="What it is about, in a few words." error={errors.title} required>
          <input
            type="text"
            value={form.title}
            onChange={(event) => set({ title: event.target.value })}
            placeholder="Water is off on Saturday"
            required
            autoFocus
          />
        </Field>

        <Field label="The notice" error={errors.body} required>
          <textarea
            rows="5"
            value={form.body}
            onChange={(event) => set({ body: event.target.value })}
            placeholder="CAMWATER are working on the line this Saturday, so there will be no water between 8am and 4pm. Please fill your buckets on Friday night."
            required
          />
        </Field>

        {editing ? null : (
          <div className="grid-2">
            <Field label="Who gets it" error={errors.audience}>
              <select value={form.audience} onChange={(event) => set({ audience: event.target.value })}>
                <option value="all_tenants">
                  All my tenants{tenantCount ? ` (${tenantCount})` : ''}
                </option>
                <option value="property">Only one property</option>
              </select>
            </Field>

            {form.audience === 'property' ? (
              <Field label="Which property" error={errors.propertyId} required>
                <select
                  value={form.propertyId}
                  onChange={(event) => set({ propertyId: event.target.value })}
                  required
                >
                  <option value="">Choose...</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.title}
                      {property.city ? ` - ${property.city}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>
        )}

        <div className="grid-2">
          <Field
            label="Take it down on"
            hint="Optional. After this day it stops appearing. Leave empty to keep it up until you take it down."
            error={errors.expiresOn}
          >
            <input
              type="date"
              value={form.expiresOn}
              onChange={(event) => set({ expiresOn: event.target.value })}
            />
          </Field>

          <Field label="How important" hint="Urgent notices sit at the top, in the warning colour.">
            <label className="check-line">
              <input
                type="checkbox"
                checked={form.urgent}
                onChange={(event) => set({ urgent: event.target.checked })}
              />
              <span>Mark it urgent</span>
            </label>
          </Field>
        </div>
      </form>
    </Modal>
  );
};

/** Who has read a notice and who has not. */
const ReadersModal = ({ notice, onClose, onResent }) => {
  const toast = useToast();
  const detail = useFetch(`/landlord/announcements/${notice.id}`, [notice.id]);
  const [resending, setResending] = useState(false);
  const readers = detail.data?.readers || [];
  const deliveries = detail.data?.deliveries || [];
  const waiting = deliveries.filter((row) => row.queued);
  const refused = deliveries.filter((row) => !row.delivered && !row.queued);
  const posted = deliveries.length - waiting.length - refused.length;

  const resend = async () => {
    setResending(true);
    try {
      const result = await api.post(`/landlord/announcements/${notice.id}/resend`);
      toast.success(`Sending it again to ${result.queued} ${result.queued === 1 ? 'person' : 'people'}.`);
      detail.reload();
      onResent();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResending(false);
    }
  };

  // Something to send: copies refused, or a notice with no copy on record at
  // all. The server works out exactly who, and never re-sends an accepted copy.
  const canResend = !notice.withdrawn && waiting.length === 0 && (refused.length > 0 || deliveries.length < notice.sentTo || deliveries.length === 0);

  return (
    <Modal
      title={notice.title}
      onClose={onClose}
      wide
      footer={
        <>
          {canResend ? (
            <button type="button" className="btn btn-primary" onClick={resend} disabled={resending}>
              {resending ? 'Sending...' : 'Email it again to anybody who did not get it'}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <p className="modal-lead">{notice.body}</p>

      {detail.loading && !detail.data ? (
        <Spinner />
      ) : (
        <>
          {/* Two different questions, and they are not the same: whether the
              mail server took it, and whether anybody has opened it. */}
          {deliveries.length === 0 ? (
            <div className="notice notice-warn">
              <strong>There is no record of this notice being emailed.</strong> It went out before each email was
              written down, so whether it reached anybody's inbox is not known. It is on their dashboard and in their
              notifications either way. Email it again below to be sure.
            </div>
          ) : (
            <div className={`notice ${refused.length > 0 ? 'notice-warn' : waiting.length > 0 ? 'notice-info' : 'notice-good'}`}>
              <strong>
                {waiting.length > 0
                  ? `${posted} of ${deliveries.length} posted so far, ${waiting.length} still going out.`
                  : refused.length === 0
                    ? `Posted to all ${deliveries.length} of them.`
                    : `${posted} of ${deliveries.length} posted.`}
              </strong>{' '}
              {waiting.length > 0
                ? 'They go one at a time; close this and look again in a minute.'
                : refused.length === 0
                  ? 'The mail server accepted every copy. If somebody says it never arrived, it is worth them checking their spam folder.'
                  : 'The mail server would not take the rest:'}
              {refused.length > 0 ? (
                <ul className="plain-list">
                  {refused.map((row) => (
                    <li key={row.email}>
                      <strong>{row.name}</strong> ({row.email}) - {row.detail}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}

          {/* WhatsApp by hand: each button opens WhatsApp on this phone or
              computer with the notice written out, to that one tenant. */}
          {deliveries.some((row) => whatsappLink(row.phone)) ? (
            <div className="whatsapp-list">
              <strong>Send it on WhatsApp too</strong>
              <span className="muted"> One tap each opens WhatsApp with the notice written out; you press send.</span>
              <div className="row-actions">
                {deliveries
                  .filter((row) => whatsappLink(row.phone))
                  .map((row) => (
                    <a
                      key={row.email}
                      className={`btn btn-sm ${readers.some((reader) => reader.name === row.name) ? 'btn-ghost' : 'btn-outline'}`}
                      href={whatsappLink(row.phone, `*${notice.title}*\n\n${notice.body}`)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp {row.name}
                    </a>
                  ))}
              </div>
            </div>
          ) : null}

          <div className="notice notice-info">
            <strong>
              {readers.length} of {notice.sentTo} {notice.sentTo === 1 ? 'person has' : 'people have'} read it.
            </strong>{' '}
            A notice stays on somebody's dashboard until they dismiss it, so anybody missing here has not opened the
            app since it went out.
          </div>

          {readers.length ? (
            <DataTable
              columns={[
                { key: 'who', header: 'Read by', render: (reader) => reader.name },
                { key: 'when', header: 'When', render: (reader) => timeAgo(reader.read_at) }
              ]}
              rows={readers}
              rowKey={(reader) => reader.id}
              empty={{ title: 'Nobody yet' }}
            />
          ) : null}
        </>
      )}
    </Modal>
  );
};

const Announcements = () => {
  useTitle('Notices');
  const toast = useToast();
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false);

  const notices = useFetch('/landlord/announcements');

  if (notices.loading && !notices.data) return <Spinner />;
  if (notices.error) {
    return (
      <div className="container section">
        <ErrorState message={notices.error} onRetry={notices.reload} />
      </div>
    );
  }

  const { items, properties, tenantCount } = notices.data;

  const columns = [
    {
      key: 'notice',
      header: 'Notice',
      render: (notice) => (
        <div className="cell-stack">
          <button type="button" className="cell-title link-btn" onClick={() => setViewing(notice)}>
            {notice.title}
          </button>
          <span className="muted">{notice.body.length > 90 ? `${notice.body.slice(0, 90)}...` : notice.body}</span>
          {notice.postedByName ? <span className="muted">Put out by {notice.postedByName}</span> : null}
        </div>
      )
    },
    {
      key: 'who',
      header: 'Who got it',
      render: (notice) => (
        <div className="cell-stack">
          <span>{notice.audience === 'property' ? notice.property?.title || 'One property' : 'Everybody'}</span>
          <span className="muted">
            {notice.sentTo} {notice.sentTo === 1 ? 'person' : 'people'}
          </span>
        </div>
      )
    },
    {
      key: 'read',
      header: 'Posted / read',
      render: (notice) => (
        <div className="cell-stack">
          <span className={notice.emailFailed > 0 || (!notice.emailed && !notice.emailWaiting) ? 'overdue-flag' : 'muted'}>
            {notice.emailFailed > 0
              ? `${notice.emailFailed} could not be posted`
              : notice.emailWaiting > 0
                ? `emailed to ${notice.emailed ?? 0}, ${notice.emailWaiting} going out`
                : notice.emailed
                  ? `emailed to ${notice.emailed}`
                  : 'no email on record'}
          </span>
          <button type="button" className="link-btn" onClick={() => setViewing(notice)}>
            {notice.reads} of {notice.sentTo} read it
          </button>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (notice) => (
        <div className="cell-stack">
          <StatusBadge
            status={notice.withdrawn ? 'withdrawn' : notice.urgent ? 'rejected' : 'approved'}
            label={notice.withdrawn ? 'Taken down' : notice.urgent ? 'Urgent' : 'Up'}
          />
          <span className="muted">{timeAgo(notice.createdAt)}</span>
          {notice.expiresOn && !notice.withdrawn ? (
            <span className="muted">Until {formatDate(notice.expiresOn)}</span>
          ) : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (notice) =>
        notice.withdrawn ? null : (
          <div className="row-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(notice)}>
              Change
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWithdrawing(notice)}>
              Take it down
            </button>
          </div>
        )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Notices</h1>
          <p className="muted">
            One thing said to all your tenants at once - the water, the gate, the generator. It reaches everybody on a
            lease, everybody on your meters, and every tenant account you have set up, and lands on their dashboard, in
            their notifications and in their inbox.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setComposing(true)}>
          Put out a notice
        </button>
      </header>

      {tenantCount === 0 ? (
        <div className="notice notice-info">
          <strong>You have no tenants yet.</strong> Once somebody is on a lease or one of your meters, anything you put
          out here reaches them.
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'Nothing has been put out yet',
          message: 'Tell everybody about a water cut, a gate code or a change to how rent is collected.'
        }}
      />

      {composing ? (
        <ComposeModal
          properties={properties}
          tenantCount={tenantCount}
          onClose={() => setComposing(false)}
          onSubmit={async (payload) => {
            const result = await api.post('/landlord/announcements', payload);
            toast.success(
              `Sent to ${result.announcement.sentTo} ${result.announcement.sentTo === 1 ? 'person' : 'people'}. ` +
                'A copy is on its way to you too.'
            );
            setComposing(false);
            notices.reload();
          }}
        />
      ) : null}

      {editing ? (
        <ComposeModal
          properties={properties}
          tenantCount={tenantCount}
          notice={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            await api.patch(`/landlord/announcements/${editing.id}`, payload);
            toast.success('Changed. Their dashboard shows the new wording.');
            setEditing(null);
            notices.reload();
          }}
        />
      ) : null}

      {viewing ? (
        <ReadersModal notice={viewing} onClose={() => setViewing(null)} onResent={notices.reload} />
      ) : null}

      {withdrawing ? (
        <Confirm
          title={`Take down "${withdrawing.title}"`}
          message="It comes off everybody's dashboard straight away. What was emailed cannot be recalled, and this page keeps the record of what was said."
          confirmLabel="Take it down"
          busy={busy}
          onConfirm={async () => {
            setBusy(true);
            try {
              await api.del(`/landlord/announcements/${withdrawing.id}`);
              toast.success('Taken down.');
              setWithdrawing(null);
              notices.reload();
            } catch (error) {
              toast.error(error.message);
            } finally {
              setBusy(false);
            }
          }}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
    </div>
  );
};

export default Announcements;
